import {
  useState,
  useCallback,
  useMemo,
  Dispatch,
  SetStateAction,
} from "react";
import { Badge, Button } from "@mui/material";
import { FilterList } from "@mui/icons-material";
import IncludeExcludeFilter, {
  IncludeExcludeFilterType,
} from "../../../components/include-exclude-filter/IncludeExcludeFilter";
import { FilterSortGroup } from "../projectTypes";
import { ProjectIndividual } from "../canonical";

interface ProjectDetailsDashWidgetFilterProps {
  filterSortGroup: FilterSortGroup;
  setFilterSortGroup: Dispatch<SetStateAction<FilterSortGroup>>;
}

export default function ProjectDetailsDashWidgetFilter({
  filterSortGroup,
  setFilterSortGroup,
}: Readonly<ProjectDetailsDashWidgetFilterProps>) {
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const handleFilterChange = useCallback(
    (filters: IncludeExcludeFilterType) => {
      setFilterSortGroup((prev: FilterSortGroup) => ({
        ...prev,
        filters: {
          ...prev?.filters,
          currentFilters: filters,
        },
      }));
    },
    [setFilterSortGroup],
  );

  const handleApplyFilters = useCallback(
    (filters: IncludeExcludeFilterType) => {
      handleFilterChange(filters);
      setOpenFilterDialog(false);
    },
    [handleFilterChange],
  );

  const handleCancel = () => setOpenFilterDialog(false);

  const isIncludeExcludeFilterType = (
    obj: any,
  ): obj is IncludeExcludeFilterType =>
    obj && typeof obj === "object" && "include" in obj && "exclude" in obj;

  const activeFilterCount = useMemo(() => {
    const currentRaw = filterSortGroup?.filters?.currentFilters as any;
    const current = isIncludeExcludeFilterType(currentRaw) ? currentRaw : null;
    if (!current) return 0;

    const countValues = (record: Record<string, any>) =>
      Object.values(record).reduce((acc, val) => {
        if (!val) return acc;
        if (Array.isArray(val)) {
          if (
            val.length === 2 &&
            (val[0]?.isValid || val[1]?.isValid) &&
            typeof val[0] !== "string"
          ) {
            return acc + 1; // date range selected
          }
          return acc + val.length; // checkbox selections
        }
        return acc;
      }, 0);
    return (
      countValues(current.include || {}) + countValues(current.exclude || {})
    );
  }, [filterSortGroup?.filters?.currentFilters]);

  return (
    <>
      <Button
        color="inherit"
        variant="text"
        startIcon={
          <Badge
            color="secondary"
            badgeContent={activeFilterCount}
            invisible={activeFilterCount === 0}
            max={99}
          >
            <FilterList />
          </Badge>
        }
        onClick={() => setOpenFilterDialog(true)}
        sx={{ color: "text.secondary" }}
      >
        FILTER
      </Button>
      <IncludeExcludeFilter
        categories={filterSortGroup?.filters.categories}
        openFilter={openFilterDialog}
        onCloseFilter={() => setOpenFilterDialog(false)}
        onApplyFilters={handleApplyFilters}
        onCancel={handleCancel}
        existingFilters={
          isIncludeExcludeFilterType(filterSortGroup?.filters.currentFilters)
            ? (filterSortGroup?.filters
                .currentFilters as IncludeExcludeFilterType)
            : undefined
        }
        sortItems={false}
      />
    </>
  );
}
